import React, { FC, useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import StockService from '../../Api_Module/Api_Services/StockService';

type TimePoint = { timestamp: string;[k: string]: number | string | null | undefined };

type ApiResponse = {
  symbol: string;
  series?: TimePoint[];
  chartPoints?: Array<{
    timestamp: string;
    close?: number | null;
    metrics?: Record<string, number | null>;
    formattedMetrics?: Record<string, string>;
  }>;
  events?: Record<string, Array<{
    timestamp: string;
    value?: number | null;
    formattedValue?: string;
    fiscalYear?: string;
    period?: string;
    displayLabel?: string;
  }>>;
  metadata?: {
    symbol: string;
    period: string;
    metricsConfig: any[];
    totalPoints: number;
    requestFrom?: string;
    requestTo?: string;
    priceMinDate?: string;
    priceMaxDate?: string;
    financialMinDate?: string;
    financialMaxDate?: string;
  };
};

interface CompanyMetricPair {
  company: string;
  metric: Metric;
  color: string;
}

type Props = {
  symbol?: string;
  symbols?: string[];
  comparisonSymbol?: string;
  metrics?: string[];
  companyMetricPairs?: CompanyMetricPair[];
  period: 'annual' | 'quarter';
  includePrice?: boolean;
  height?: number;
  from?: string;
  to?: string;
};

function toEpoch(dateStr: string): number {
  const d = new Date(`${dateStr}T00:00:00Z`);
  return d.getTime();
}

// Derive a calendar-year "bucket" for an annual event point (FY/TTM).
// FY'23 -> 2023, TTM -> year of its timestamp, fallback -> calendar year.
function getBucketYearFromEvent(p: { timestamp: string; displayLabel?: string; period?: string; fiscalYear?: string | number }): number | null {
  const label = p.displayLabel?.toUpperCase();
  const ts = toEpoch(p.timestamp);
  if (!Number.isFinite(ts)) return null;

  if (label?.startsWith('FY')) {
    const yy = parseInt(label.replace(/[^0-9]/g, ''), 10);
    if (!Number.isFinite(yy)) return new Date(ts).getUTCFullYear();
    // Handle 2-digit years: 00-50 -> 2000s, 51-99 -> 1900s
    if (yy < 100) {
      return yy <= 50 ? 2000 + yy : 1900 + yy;
    }
    return yy;
  }

  if (label === 'TTM') {
    return new Date(ts).getUTCFullYear();
  }

  return new Date(ts).getUTCFullYear();
}

function normalizeKey(k: string): string {
  return String(k || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function stripSuffixes(nk: string): string {
  return nk.replace(/_(TTM|YOY|QOQ|MRQ|LFY|AVG|MEAN|MEDIAN|STD|MIN|MAX)$/g, '');
}

function buildMatcher(filterSet: Set<string> | null) {
  if (!filterSet || filterSet.size === 0) return (_: string) => true;

  const baseFilters = Array.from(filterSet).map(stripSuffixes);
  const baseFilterSet = new Set(baseFilters);
  const baseCollapsed = baseFilters.map(f => f.replace(/_/g, ''));

  return (candidate: string) => {
    const c = stripSuffixes(candidate);
    const cCollapsed = c.replace(/_/g, '');

    // Exact match on normalized or base form
    if (filterSet.has(candidate) || baseFilterSet.has(c)) return true;

    // Match ignoring underscores (e.g. PRICE_TO_EARNINGS_RATIO vs PRICETOEARNINGSRATIO)
    for (let i = 0; i < baseFilters.length; i++) {
      const f = baseFilters[i];
      const fCollapsed = baseCollapsed[i];
      if (cCollapsed === fCollapsed) return true;
      if (cCollapsed.includes(fCollapsed) || fCollapsed.includes(cCollapsed)) return true;
      if (c.includes(f) || f.includes(c)) return true;
    }

    return false;
  };
}

function medianAbs(values: number[]): number {
  const a = values.filter(v => Number.isFinite(v)).map(v => Math.abs(v)).sort((x, y) => x - y);
  if (a.length === 0) return 0;
  const mid = Math.floor(a.length / 2);
  return a.length % 2 ? a[mid] : (a[mid - 1] + a[mid]) / 2;
}

function bigNumberLabel(v: number): string {
  const av = Math.abs(v);
  if (av >= 1e12) return `${Highcharts.numberFormat(v / 1e12, 2)} T`;
  if (av >= 1e9) return `${Highcharts.numberFormat(v / 1e9, 2)} B`;
  if (av >= 1e6) return `${Highcharts.numberFormat(v / 1e6, 2)} M`;
  if (av >= 1e3) return `${Highcharts.numberFormat(v / 1e3, 0)} K`;
  return Highcharts.numberFormat(v, 2);
}

function toNumber(v: any): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (v == null) return null;
  const n = parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

function parseFiscalYearFromLabel(label: string): number | null {
  const m = label.match(/\d{2,4}/);
  if (!m) return null;
  const raw = m[0];
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return null;
  if (raw.length === 4) return n;
  if (n <= 50) return 2000 + n;
  return 1900 + n;
}

function getFiscalYearFromEvent(p: { fiscalYear?: string; displayLabel?: string; timestamp: string }): number | null {
  if (p.fiscalYear) {
    const fy = parseInt(p.fiscalYear, 10);
    if (Number.isFinite(fy)) return fy;
  }
  if (p.displayLabel) {
    return parseFiscalYearFromLabel(p.displayLabel);
  }
  return null;
}

function parseQuarterFromLabel(label: string): { year: number; quarter: number } | null {
  const year = parseFiscalYearFromLabel(label);
  if (year == null) return null;
  const qm = label.match(/Q(\d)/i);
  if (!qm) return null;
  const q = parseInt(qm[1], 10);
  if (!Number.isFinite(q) || q < 1 || q > 4) return null;
  return { year, quarter: q };
}

// Format metric names for display (PRICE_TO_EARNINGS_RATIO -> Price To Earnings Ratio)
function formatMetricName(name: string): string {
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}



// Revert to simple forward fill for accounting metrics (Margins, Ratios)
function forwardFillToDaily(xs: number[], events: Array<{ x: number; y: number | null }>): Array<[number, number | null]> {
  if (!xs || xs.length === 0) return [];
  const ev = events
    .filter(e => e && typeof e.x === 'number')
    .sort((a, b) => a.x - b.x);
  let idx = 0;
  let current: number | null = null;
  const out: Array<[number, number | null]> = [];
  for (const x of xs) {
    while (idx < ev.length && ev[idx].x <= x) {
      current = ev[idx].y ?? current;
      idx++;
    }
    out.push([x, current]);
  }
  return out;
}

function isRatioMetricName(name: string): boolean {
  const n = name.toUpperCase();
  return (
    n.includes('RATIO') ||
    n.includes('MARGIN') ||
    n.includes('YIELD') ||
    n.includes('GROWTH') ||  // Growth metrics should be displayed as lines
    n.includes('EV_') ||
    n === 'PE' || n.startsWith('PE_') || n.endsWith('_PE') || n.includes('_PE_') ||
    n.includes('P/E') ||
    n === 'PB' || n.startsWith('PB_') || n.endsWith('_PB') || n.includes('_PB_') ||
    n === 'PS' || n.startsWith('PS_') || n.endsWith('_PS') || n.includes('_PS_') ||
    n.includes('EV TO') ||
    n.includes('EV-TO') ||
    n.includes('EV/SALE') ||
    n.includes('EV_TO_SALES')
  );
}

function guessAxisIndex(key: string, sample: number[]): 0 | 1 | 2 {
  if (key === 'close') return 0;
  if (isRatioMetricName(key)) return 1;
  const m = medianAbs(sample);
  if (m <= 1000) return 1;
  return 2;
}

export function buildHighchartsOptions(
  api: ApiResponse,
  opts: {
    period: 'annual' | 'quarter';
    includePrice?: boolean;
    height?: number;
    metricsFilter?: string[];
    comparisonApi?: ApiResponse | null;
    multiCompanyData?: Map<string, ApiResponse>;
    companyMetricPairs?: CompanyMetricPair[];
  } = { period: 'annual' }
): Highcharts.Options {
  const includePrice = opts.includePrice === true; // Only include price if explicitly requested
  const priceColor = '#3b82f6';
  const ratioPalette = ['#2a6cf0', '#8b5cf6', '#06b6d4', '#f43f5e', '#10b981', '#ef4444'];
  // Removed Orange (#f59e0b) from palette to avoid conflict with Revenue, added Blue and others
  const columnPalette = ['#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899'];

  // Build a color map from companyMetricPairs for consistent colors
  const pairColorMap = new Map<string, string>();
  if (opts.companyMetricPairs) {
    opts.companyMetricPairs.forEach(pair => {
      const metricKey = normalizeKey(pair.metric.metricAttributeName || '');
      const key = `${pair.company}-${metricKey}`;
      pairColorMap.set(key, pair.color);
    });
  }

  const yAxes: Highcharts.YAxisOptions[] = [
    {
      title: { text: 'Price', style: { color: 'var(--text-secondary)' } },
      labels: { formatter: function () { return `$${Highcharts.numberFormat(this.value as number, 2)} `; }, style: { color: 'var(--text-secondary)' } },
      opposite: false,
      gridLineColor: '#E5E7EB', // Modern light gray
      visible: includePrice
    },
    {
      title: { text: '' },
      labels: { formatter: function () { return Highcharts.numberFormat(this.value as number, 2); }, style: { color: 'var(--text-secondary)' } },
      opposite: true,
      gridLineColor: 'transparent',
      offset: 50 // Offset to avoid overlap with Financials
    },
    {
      title: { text: '' },
      labels: { formatter: function () { return bigNumberLabel(this.value as number); }, style: { color: 'var(--text-secondary)' } },
      opposite: true, // Moved to right side
      gridLineColor: '#E5E7EB' // Modern light gray
    }
  ];



  const series: Highcharts.SeriesOptionsType[] = [];
  // Track what's being rendered to decide the x-axis cap
  let hasEventColumns = false;
  let eventsFirstKeyLastMs = 0;
  let eventsFirstKeySet = false;
  let hasDailyLineData = false; // Track if we have daily line data (like P/E from chartPoints)

  const pendingColumns: Array<{ key: string; displayArr: Array<{ timestamp: string; value: number | null; displayLabel?: string; formattedValue?: string }>; yAxisIndex: number; color: string; companySymbol?: string }>
    = [];

  const timestampToDisplayLabel = new Map<number, string>();
  const labelToMinTimestamp = new Map<string, number>();

  // Check if we have multiple companies (multiCompanyData has 2+ entries, or we have companyMetricPairs with 2+ companies)
  const multiCompanyCount = opts.multiCompanyData?.size || 0;
  const uniqueCompanies = opts.companyMetricPairs ? new Set(opts.companyMetricPairs.map(p => p.company)).size : 0;
  const isMultiCompany = multiCompanyCount > 1 || uniqueCompanies > 1;
  const useDisplayLabelCategories = isMultiCompany && opts.period === 'annual';
  // Collect all unique displayLabels across all companies for category axis
  const allDisplayLabels = new Set<string>();

  // Get date range filter from metadata - filter out data before requestFrom
  const requestFromMs = api.metadata?.requestFrom ? toEpoch(api.metadata.requestFrom) : 0;
  const requestToMs = api.metadata?.requestTo ? toEpoch(api.metadata.requestTo) : Infinity;
  const requestFromYear = api.metadata?.requestFrom
    ? new Date(`${api.metadata.requestFrom}T00:00:00Z`).getUTCFullYear()
    : undefined;
  const requestToYear = api.metadata?.requestTo
    ? new Date(`${api.metadata.requestTo}T00:00:00Z`).getUTCFullYear()
    : undefined;

  // Helper function to extract numeric value from event point
  // Prefers formattedValue (new API format), falls back to value (old format)
  const extractEventValue = (p: any): number | null => {
    if (p.formattedValue !== undefined && p.formattedValue !== null) {
      const str = String(p.formattedValue).trim().toUpperCase();

      // Remove % sign if present (for percentages)
      const cleanStr = str.replace('%', '');

      // Check for suffixes (B, M, K, T)
      let multiplier = 1;
      let numStr = cleanStr;

      if (cleanStr.endsWith('T')) {
        multiplier = 1e12;
        numStr = cleanStr.slice(0, -1);
      } else if (cleanStr.endsWith('B')) {
        multiplier = 1e9;
        numStr = cleanStr.slice(0, -1);
      } else if (cleanStr.endsWith('M')) {
        multiplier = 1e6;
        numStr = cleanStr.slice(0, -1);
      } else if (cleanStr.endsWith('K')) {
        multiplier = 1e3;
        numStr = cleanStr.slice(0, -1);
      }

      const parsed = parseFloat(numStr);
      return Number.isFinite(parsed) ? parsed * multiplier : null;
    }
    return typeof p.value === 'number' ? p.value : null;
  };

  // Get primary company symbol for series naming
  const primarySymbol = api.symbol || '';

  // Prefer chartPoints when available, otherwise fall back to legacy series
  const dailyRaw: any[] = (Array.isArray((api as any).chartPoints) && (api as any).chartPoints.length > 0)
    ? ((api as any).chartPoints as any[]).sort((a, b) => toEpoch(String(a.timestamp)) - toEpoch(String(b.timestamp)))
    : (Array.isArray(api.series) ? (api.series as any[]).sort((a, b) => toEpoch(String(a.timestamp)) - toEpoch(String(b.timestamp))) : []);

  const metricFilterSet = (opts.metricsFilter && opts.metricsFilter.length > 0)
    ? new Set(opts.metricsFilter.map(k => normalizeKey(k)))
    : null;
  console.log('[FinancialChart] metricsFilter:', opts.metricsFilter);
  console.log('[FinancialChart] metricFilterSet:', metricFilterSet ? Array.from(metricFilterSet) : null);
  const matchesFilter = buildMatcher(metricFilterSet);

  const topLevelDailyKeys = new Set<string>();
  const nestedMetricKeys = new Set<string>();

  // Build map of daily prices for ratio calculation
  const dailyPrices = new Map<number, number>();

  dailyRaw.forEach((row: any) => {
    if (row && typeof row === 'object') {
      const ts = toEpoch(String(row.timestamp));
      if (typeof row.close === 'number') {
        dailyPrices.set(ts, row.close);
      }

      Object.keys(row).forEach(k => {
        if (k !== 'timestamp' && k !== 'metrics' && k !== 'formattedMetrics') topLevelDailyKeys.add(k);
      });
      // Prefer formattedMetrics when available, fallback to metrics for backward compatibility
      const metricsSource = row.formattedMetrics || row.metrics;
      if (metricsSource && typeof metricsSource === 'object') {
        Object.keys(metricsSource).forEach((mk) => nestedMetricKeys.add(mk));
      }
    }
  });

  // Track keys that we have found in daily data so we don't duplicate them from events
  const processedKeys = new Set<string>();
  topLevelDailyKeys.forEach(k => processedKeys.add(normalizeKey(k)));
  nestedMetricKeys.forEach(k => processedKeys.add(normalizeKey(k)));

  // If every requested metric is absent from daily data, we are in events-only mode
  const onlyEventMetricsSelected = Array.isArray(opts.metricsFilter) && opts.metricsFilter.length > 0
    ? opts.metricsFilter.every(k => !processedKeys.has(normalizeKey(k)))
    : false;

  let ratioIdx = 0;
  if (includePrice && !onlyEventMetricsSelected && (topLevelDailyKeys.has('close') || dailyRaw.some(p => typeof p.close === 'number'))) {
    const priceData = dailyRaw.map(p => [toEpoch(String(p.timestamp)), typeof p.close === 'number' ? p.close : null]);
    series.push({
      type: 'area',
      name: 'Close',
      data: priceData as any,
      yAxis: 0,
      color: priceColor,
      lineWidth: 1.5,
      fillOpacity: 0.15,
      dataGrouping: { enabled: true }

    } as Highcharts.SeriesAreaOptions);
  }


  // Process daily metrics (like P/E ratio) from chartPoints, regardless of price availability
  // Skip if we're in multi-company mode (will be processed in the multi-company loop)
  const isMultiCompanyMode = opts.multiCompanyData && opts.multiCompanyData.size > 0;
  if (!onlyEventMetricsSelected && !isMultiCompanyMode) {
    Array.from(topLevelDailyKeys)
      .filter(k => k !== 'close')
      .filter(k => matchesFilter(normalizeKey(k)))
      .forEach((key) => {
        const values = dailyRaw.map((d: any) => (typeof d[key] === 'number' ? (d[key] as number) : NaN)).filter((v: number) => Number.isFinite(v));
        if (values.length === 0) return;
        const yAxisIndex = guessAxisIndex(key, values as number[]);
        const data = dailyRaw.map((d: any) => [toEpoch(String(d.timestamp)), typeof d[key] === 'number' ? (d[key] as number) : null]);
        series.push({
          type: 'line',
          name: formatMetricName(key),
          data: data as any,
          yAxis: yAxisIndex,
          color: ratioPalette[ratioIdx % ratioPalette.length],
          lineWidth: 1.8,
          marker: { enabled: false },
          connectNulls: true,
          dataGrouping: { enabled: true }
        } as Highcharts.SeriesLineOptions);
        ratioIdx += 1;
      });



    Array.from(nestedMetricKeys)
      .filter(k => matchesFilter(normalizeKey(k)))
      .forEach((key) => {
        const sampleVals = dailyRaw.map((d: any) => {
          // Prefer formattedMetrics, fallback to metrics for backward compatibility
          const formattedVal = d?.formattedMetrics?.[key];
          const rawVal = d?.metrics?.[key];
          const v = formattedVal !== undefined ? formattedVal : rawVal;
          return typeof v === 'number' ? v : (v != null ? parseFloat(String(v)) : NaN);
        }).filter((v: number) => Number.isFinite(v));
        if (sampleVals.length === 0) return;
        const yAxisIndex = guessAxisIndex(key, sampleVals as number[]);
        const data = dailyRaw.map((d: any) => [
          toEpoch(String(d.timestamp)),
          (() => {
            // Prefer formattedMetrics, fallback to metrics for backward compatibility
            const formattedVal = d?.formattedMetrics?.[key];
            const rawVal = d?.metrics?.[key];
            const v = formattedVal !== undefined ? formattedVal : rawVal;
            if (typeof v === 'number') return v;
            if (v == null) return null;
            const n = parseFloat(String(v));
            return Number.isFinite(n) ? n : null;
          })()
        ]);
        series.push({
          type: 'line',
          name: formatMetricName(key),
          data: data as any,
          yAxis: yAxisIndex,
          color: ratioPalette[ratioIdx % ratioPalette.length],
          lineWidth: 1.8,
          marker: { enabled: false },
          connectNulls: true,
          clip: false, // Allow line to render beyond plot area
          dataGrouping: { enabled: true }
        } as Highcharts.SeriesLineOptions);
        ratioIdx += 1;

      });
  }




  // Process events for primary company (Revenue, etc.)
  // Skip if we're in multi-company mode (will be processed in the multi-company loop)
  const pointRangeDays = opts.period === 'annual' ? 320 : 75;
  const prMs = 24 * 3600 * 1000 * pointRangeDays;
  let colIdx = 0;

  if (!isMultiCompanyMode) {
    const events = api.events || {};
    Object.keys(events).forEach((ek) => {
      const normKey = normalizeKey(ek);
      if (!matchesFilter(normKey)) return;

      const arr = events[ek] || [];
      if (!Array.isArray(arr) || arr.length === 0) return;
      const vals = arr.map(p => {
        const v = extractEventValue(p);
        return v !== null ? v : NaN;
      }).filter(v => Number.isFinite(v));
      const yAxisIndex = guessAxisIndex(ek, vals);

      // Rely on the axis index (derived from value magnitude) to decide if it's a ratio (Line) or Amount (Column)
      const isRatio = yAxisIndex === 1;
      // For non-ratio metrics, if we already have this metric from daily chartPoints,
      // skip the event version to avoid duplicates. For ratios (P/E, margins, etc.),
      // always use the event-based series even if daily data exists.
      if (!isRatio && processedKeys.has(normKey)) return;
      if (isRatio) {
        const dailyXs = dailyRaw.map(p => toEpoch(String(p.timestamp)));
        const eventPoints = arr.map(p => ({
          x: toEpoch(p.timestamp),
          y: toNumber(extractEventValue(p))
        }));

        let lineData: Array<[number, number | null]>;

        if (dailyXs.length > 0) {
          // Use Step (Forward Fill) for any ratio not in daily data (likely accounting metrics)
          lineData = forwardFillToDaily(dailyXs, eventPoints);
        } else {
          lineData = eventPoints.map(p => [p.x, p.y] as [number, number | null]);
        }

        // Get color from pairColorMap if available
        const ratioPairKey = `${primarySymbol}-${normalizeKey(ek)}`;
        const ratioPairColor = pairColorMap.get(ratioPairKey) || ratioPalette[ratioIdx % ratioPalette.length];
        // Include company symbol in name if we have multiple companies
        const ratioSeriesName = primarySymbol && opts.multiCompanyData && opts.multiCompanyData.size > 1
          ? `${primarySymbol} - ${formatMetricName(ek)}`
          : formatMetricName(ek);

        // Growth metrics should be smooth lines, not stepped
        const isGrowthMetric = normKey.includes('GROWTH') || normKey.includes('_GROWTH');

        series.push({
          type: 'line',
          name: ratioSeriesName,
          data: lineData as any,
          yAxis: 1,
          color: ratioPairColor,
          lineWidth: 1.8,
          marker: { enabled: false },
          connectNulls: true,
          step: isGrowthMetric ? undefined : 'left', // Only step for non-growth metrics
          dataGrouping: { enabled: true }
        } as Highcharts.SeriesLineOptions);
        ratioIdx += 1;
      } else {

        // For annual view, keep only FY (full year) and TTM entries, filter out quarterly (Q1, Q2, Q3, Q4)
        // Also filter by requestFrom **year** from metadata (e.g. keep FY >= 2020), always include TTM
        let displayArr: Array<{ timestamp: string; value: number | null; displayLabel?: string }> = arr;
        if (opts.period === 'annual') {
          // Deduplicate by displayLabel - this groups all entries with the same label
          const perLabel = new Map<string, { timestamp: string; value: number | null; displayLabel?: string }>();
          arr.forEach(p => {
            const t = toEpoch(p.timestamp);
            const isTTM = p.displayLabel?.toUpperCase() === 'TTM';

            // Determine if this is an annual entry we should keep
            let shouldKeep = false;
            let key = '';

            if (p.displayLabel) {
              const label = p.displayLabel.toUpperCase();
              // Keep only FY* and TTM entries
              shouldKeep = label.startsWith('FY') || label === 'TTM';
              key = p.displayLabel;
            } else if (p.period) {
              const period = p.period.toUpperCase();
              shouldKeep = period === 'FY';
              key = p.fiscalYear ? `FY'${String(p.fiscalYear).slice(-2)}` : p.timestamp;
            }

            if (!shouldKeep) return;

            // Additional year-based filtering for non-TTM entries in annual mode
            if (!isTTM && requestFromYear !== undefined) {
              const fy = getFiscalYearFromEvent(p);
              if (fy != null) {
                if (fy < requestFromYear) return;
              } else if (requestFromMs > 0 && t < requestFromMs) {
                // Fallback to timestamp filter if fiscal year is unavailable
                return;
              }
            }

            const existing = perLabel.get(key);
            if (!existing || t > toEpoch(existing.timestamp)) {
              perLabel.set(key, { timestamp: p.timestamp, value: extractEventValue(p), displayLabel: p.displayLabel || key, formattedValue: p.formattedValue });
            }
          });
          displayArr = Array.from(perLabel.values()).sort((a, b) => toEpoch(a.timestamp) - toEpoch(b.timestamp));
        }

        // For quarterly view, keep only entries within the requested date range
        if (opts.period === 'quarter') {
          const finiteToMs = Number.isFinite(requestToMs) ? requestToMs : undefined;
          displayArr = displayArr.filter(p => {
            const t = toEpoch(p.timestamp);
            const isTTM = p.displayLabel?.toUpperCase() === 'TTM';
            if (isTTM) return true;
            const fy = getFiscalYearFromEvent(p);

            // Lower bound (from)
            if (requestFromYear !== undefined) {
              if (fy != null) {
                if (fy < requestFromYear) return false;
              } else if (requestFromMs > 0 && t < requestFromMs) {
                return false;
              }
            } else if (requestFromMs > 0 && t < requestFromMs) {
              return false;
            }

            // Upper bound (to)
            if (requestToYear !== undefined) {
              if (fy != null) {
                if (fy > requestToYear) return false;
              } else if (finiteToMs !== undefined && t > finiteToMs) {
                return false;
              }
            } else if (finiteToMs !== undefined && t > finiteToMs) {
              return false;
            }

            return true;
          });
        }

        if (!displayArr.length) return;

        // In annual mode, ensure at most one value per calendar-year bucket for this
        // metric, preferring FY over non-FY (including TTM) when both exist.
        if (opts.period === 'annual') {
          const perBucket = new Map<number, { timestamp: string; value: number | null; displayLabel?: string }>();
          displayArr.forEach(p => {
            const bucketYear = getBucketYearFromEvent(p);
            if (bucketYear == null) return;
            const existing = perBucket.get(bucketYear);
            if (!existing) {
              perBucket.set(bucketYear, p);
              return;
            }

            const existingLabel = (existing.displayLabel || '').toUpperCase();
            const newLabel = (p.displayLabel || '').toUpperCase();
            const isFY = (s: string) => s.startsWith('FY');

            // Prefer FY over non-FY (e.g. TTM) for the same bucket year
            if (isFY(newLabel) && !isFY(existingLabel)) {
              perBucket.set(bucketYear, p);
              return;
            }
            if (isFY(existingLabel) && !isFY(newLabel)) {
              return;
            }

            // If both are the same type, keep the one with the latest timestamp
            if (toEpoch(p.timestamp) > toEpoch(existing.timestamp)) {
              perBucket.set(bucketYear, p);
            }
          });

          displayArr = Array.from(perBucket.values()).sort((a, b) => toEpoch(a.timestamp) - toEpoch(b.timestamp));
        }

        // Populate bucketed year -> label map and track earliest timestamp per year
        displayArr.forEach(p => {
          if (!p.displayLabel) return;
          if (opts.period === 'annual') {
            const bucketYear = getBucketYearFromEvent(p);
            if (bucketYear == null) return;
            const x = Date.UTC(bucketYear, 0, 1);
            const key = String(bucketYear);
            timestampToDisplayLabel.set(x, key);
            allDisplayLabels.add(key);
            const existing = labelToMinTimestamp.get(key);
            if (existing === undefined || x < existing) {
              labelToMinTimestamp.set(key, x);
            }
          } else {
            const t = toEpoch(p.timestamp);
            timestampToDisplayLabel.set(t, p.displayLabel);
            allDisplayLabels.add(p.displayLabel);
            const existing = labelToMinTimestamp.get(p.displayLabel);
            if (existing === undefined || t < existing) {
              labelToMinTimestamp.set(p.displayLabel, t);
            }
          }
        });

        // Capture the last timestamp of the PRIMARY displayed events metric.
        // Prefer REVENUE if present; otherwise use the first displayed metric.
        const lastTs = displayArr
          .filter(p => typeof p.value === 'number')
          .map(p => {
            if (opts.period === 'annual' && p.displayLabel) {
              const by = getBucketYearFromEvent(p);
              return by != null ? Date.UTC(by, 0, 1) : toEpoch(p.timestamp);
            }
            return toEpoch(p.timestamp);
          })
          .reduce((max, t) => (t > max ? t : max), 0);
        if (!eventsFirstKeySet || normalizeKey(ek) === 'REVENUE') {
          eventsFirstKeyLastMs = lastTs;
          eventsFirstKeySet = true;
        }
        hasEventColumns = true;
        // Get color from pairColorMap if available, otherwise use default palette
        const pairKey = `${primarySymbol}-${normalizeKey(ek)}`;
        const pairColor = pairColorMap.get(pairKey) || columnPalette[colIdx % columnPalette.length];
        // For annual, rewrite timestamps in displayArr to bucket-year start-of-year dates
        // so that each FY/TTM bar shares the same calendar-year x position.
        const bucketedDisplayArr = displayArr.map(p => {
          if (opts.period === 'annual' && p.displayLabel) {
            const by = getBucketYearFromEvent(p);
            if (by != null) {
              return { ...p, timestamp: new Date(Date.UTC(by, 0, 1)).toISOString().slice(0, 10) };
            }
          }
          return p;
        });

        pendingColumns.push({
          key: ek,
          displayArr: bucketedDisplayArr,
          yAxisIndex,
          color: pairColor,
          companySymbol: primarySymbol
        });
        colIdx += 1;
      }
    });
  }



  // Note: pendingColumns finalization moved to after multi-company data collection

  // Process Multi-Company Data (from companyMetricPairs)
  if (opts.multiCompanyData && opts.multiCompanyData.size > 0) {
    let companyIdx = 0;
    opts.multiCompanyData.forEach((companyApi, companySymbol) => {

      // Process chartPoints/dailyRaw for this company (for ratio metrics like P/E).
      const rawChartPoints = (companyApi as any).chartPoints;
      console.log(`[Multi-Company ${companySymbol}] Raw chartPoints:`, rawChartPoints ? `${rawChartPoints.length} items` : 'null/undefined', rawChartPoints?.[0]);
      const companyDailyRaw: any[] = (Array.isArray(rawChartPoints) && rawChartPoints.length > 0)
        ? (rawChartPoints as any[]).sort((a, b) => toEpoch(String(a.timestamp)) - toEpoch(String(b.timestamp)))
        : (Array.isArray(companyApi.series) ? (companyApi.series as any[]).sort((a, b) => toEpoch(String(a.timestamp)) - toEpoch(String(b.timestamp))) : []);

      // Get keys from chartPoints
      const companyTopLevelKeys = new Set<string>();
      const companyNestedKeys = new Set<string>();
      companyDailyRaw.forEach((row: any) => {
        if (row && typeof row === 'object') {
          Object.keys(row).forEach(k => {
            if (k !== 'timestamp' && k !== 'metrics' && k !== 'close') companyTopLevelKeys.add(k);
          });
          if (row.metrics && typeof row.metrics === 'object') {
            Object.keys(row.metrics).forEach((mk) => companyNestedKeys.add(mk));
          }
        }
      });

      // Process top-level daily keys (like P/E ratio)
      Array.from(companyTopLevelKeys)
        .filter(k => matchesFilter(normalizeKey(k)))
        .forEach((key) => {
          const values = companyDailyRaw.map((d: any) => (typeof d[key] === 'number' ? (d[key] as number) : NaN)).filter((v: number) => Number.isFinite(v));
          if (values.length === 0) return;
          const yAxisIndex = guessAxisIndex(key, values as number[]);
          const data = companyDailyRaw.map((d: any) => [toEpoch(String(d.timestamp)), typeof d[key] === 'number' ? (d[key] as number) : null]);

          // Get color from pairColorMap
          const pairKey = `${companySymbol}-${normalizeKey(key)}`;
          const pairColor = pairColorMap.get(pairKey) || ratioPalette[(companyIdx + ratioIdx) % ratioPalette.length];

          series.push({
            type: 'line',
            name: `${companySymbol} - ${formatMetricName(key)}`,
            data: data as any,
            yAxis: yAxisIndex,
            color: pairColor,
            lineWidth: 1.8,
            marker: { enabled: false },
            connectNulls: true,
            dataGrouping: { enabled: true }
          } as Highcharts.SeriesLineOptions);
        });

      // Process nested metric keys (e.g. PRICE_TO_EARNINGS_RATIO inside chartPoints.metrics)
      Array.from(companyNestedKeys)
        .filter(k => {
          const matches = matchesFilter(normalizeKey(k));
          console.log(`[Multi-Company ${companySymbol}] Nested key "${k}" -> matches filter: ${matches}`);
          return matches;
        })
        .forEach((key) => {
          const sampleVals = companyDailyRaw.map((d: any) => {
            const v = d?.metrics?.[key];
            return typeof v === 'number' ? v : (v != null ? parseFloat(String(v)) : NaN);
          }).filter((v: number) => Number.isFinite(v));
          console.log(`[Multi-Company ${companySymbol}] Processing nested key "${key}": sampleVals.length=${sampleVals.length}`);
          if (sampleVals.length === 0) return;

          const yAxisIndex = guessAxisIndex(key, sampleVals as number[]);

          const data: Array<[number, number | null]> = companyDailyRaw.map((d: any) => [
            toEpoch(String(d.timestamp)),
            (() => {
              const v = d?.metrics?.[key];
              if (typeof v === 'number') return v;
              if (v == null) return null;
              const n = parseFloat(String(v));
              return Number.isFinite(n) ? n : null;
            })()
          ]);

          // Filter out completely empty series
          if (!data.some(([, y]) => y != null)) return;

          // Get color from pairColorMap
          const pairKey = `${companySymbol}-${normalizeKey(key)}`;
          const pairColor = pairColorMap.get(pairKey) || ratioPalette[(companyIdx + ratioIdx) % ratioPalette.length];

          console.log(`[Multi-Company ${companySymbol}] Adding P/E line series: ${data.length} points, yAxis=${yAxisIndex}, color=${pairColor}`);
          hasDailyLineData = true; // Mark that we have daily line data
          series.push({
            type: 'line',
            name: `${companySymbol} - ${formatMetricName(key)}`,
            data: data as any,
            yAxis: yAxisIndex,
            color: pairColor,
            lineWidth: 1.8,
            marker: { enabled: false },
            connectNulls: true,
            clip: false, // Allow line to render beyond plot area
            dataGrouping: { enabled: true },
            zIndex: 3
          } as Highcharts.SeriesLineOptions);
        });

      // Process events for this company
      const compEvents = companyApi.events || {};
      // Get date range for this company from its metadata
      const compRequestFromMs = companyApi.metadata?.requestFrom ? toEpoch(companyApi.metadata.requestFrom) : 0;
      const compRequestFromYear = companyApi.metadata?.requestFrom
        ? new Date(`${companyApi.metadata.requestFrom}T00:00:00Z`).getUTCFullYear()
        : undefined;
      const compRequestToMs = companyApi.metadata?.requestTo ? toEpoch(companyApi.metadata.requestTo) : Infinity;
      const compRequestToYear = companyApi.metadata?.requestTo
        ? new Date(`${companyApi.metadata.requestTo}T00:00:00Z`).getUTCFullYear()
        : undefined;

      console.log(`[Multi-Company ${companySymbol}] Available event keys:`, Object.keys(compEvents));
      console.log(`[Multi-Company ${companySymbol}] chartPoints top-level keys:`, Array.from(companyTopLevelKeys));
      console.log(`[Multi-Company ${companySymbol}] chartPoints nested metrics keys:`, Array.from(companyNestedKeys));
      console.log(`[Multi-Company ${companySymbol}] Metrics filter:`, opts.metricsFilter);

      Object.keys(compEvents).forEach((ek) => {
        const normKey = normalizeKey(ek);
        const matches = matchesFilter(normKey);
        console.log(`[Multi-Company ${companySymbol}] Event key "${ek}" -> normalized "${normKey}" -> matches: ${matches}`);
        if (!matches) return;
        const arr = compEvents[ek] || [];
        if (!Array.isArray(arr) || arr.length === 0) return;

        // For annual view, keep only FY and TTM entries, filter out quarterly
        // Also filter by requestFrom **year** from metadata (e.g. keep FY >= 2020), always include TTM
        let displayArr: Array<{ timestamp: string; value: number | null; displayLabel?: string }> = arr;
        if (opts.period === 'annual') {
          const perLabel = new Map<string, { timestamp: string; value: number | null; displayLabel?: string }>();
          arr.forEach(p => {
            const t = toEpoch(p.timestamp);
            const isTTM = p.displayLabel?.toUpperCase() === 'TTM';
            let shouldKeep = false;
            let key = '';

            if (p.displayLabel) {
              const label = p.displayLabel.toUpperCase();
              shouldKeep = label.startsWith('FY') || label === 'TTM';
              key = p.displayLabel;
            } else if (p.period) {
              const period = p.period.toUpperCase();
              shouldKeep = period === 'FY';
              key = p.fiscalYear ? `FY'${String(p.fiscalYear).slice(-2)}` : p.timestamp;
            }

            if (!shouldKeep) return;

            // Additional year-based filtering for non-TTM entries in annual mode
            if (!isTTM && compRequestFromYear !== undefined) {
              const fy = getFiscalYearFromEvent(p);
              if (fy != null) {
                if (fy < compRequestFromYear) return;
              } else if (compRequestFromMs > 0 && t < compRequestFromMs) {
                return;
              }
            }

            const existing = perLabel.get(key);
            if (!existing || t > toEpoch(existing.timestamp)) {
              perLabel.set(key, { timestamp: p.timestamp, value: extractEventValue(p), displayLabel: p.displayLabel || key, formattedValue: p.formattedValue });
            }
          });
          displayArr = Array.from(perLabel.values()).sort((a, b) => toEpoch(a.timestamp) - toEpoch(b.timestamp));
        }

        // For quarterly view, keep only entries within the requested date range
        if (opts.period === 'quarter') {
          const finiteToMs = Number.isFinite(compRequestToMs) ? compRequestToMs : undefined;
          displayArr = displayArr.filter(p => {
            const t = toEpoch(p.timestamp);
            const isTTM = p.displayLabel?.toUpperCase() === 'TTM';
            if (isTTM) return true;
            const fy = getFiscalYearFromEvent(p);

            // Lower bound (from)
            if (compRequestFromYear !== undefined) {
              if (fy != null) {
                if (fy < compRequestFromYear) return false;
              } else if (compRequestFromMs > 0 && t < compRequestFromMs) {
                return false;
              }
            } else if (compRequestFromMs > 0 && t < compRequestFromMs) {
              return false;
            }

            // Upper bound (to)
            if (compRequestToYear !== undefined) {
              if (fy != null) {
                if (fy > compRequestToYear) return false;
              } else if (finiteToMs !== undefined && t > finiteToMs) {
                return false;
              }
            } else if (finiteToMs !== undefined && t > finiteToMs) {
              return false;
            }

            return true;
          });
        }

        if (!displayArr.length) return;

        // In annual mode, ensure at most one value per calendar-year bucket for this
        // company/metric, preferring FY over non-FY (including TTM) when both exist.
        if (opts.period === 'annual') {
          const perBucket = new Map<number, { timestamp: string; value: number | null; displayLabel?: string }>();
          displayArr.forEach(p => {
            const bucketYear = getBucketYearFromEvent(p);
            if (bucketYear == null) return;
            const existing = perBucket.get(bucketYear);
            if (!existing) {
              perBucket.set(bucketYear, p);
              return;
            }

            const existingLabel = (existing.displayLabel || '').toUpperCase();
            const newLabel = (p.displayLabel || '').toUpperCase();
            const isFY = (s: string) => s.startsWith('FY');

            // Prefer FY over non-FY (e.g. TTM) for the same bucket year
            if (isFY(newLabel) && !isFY(existingLabel)) {
              perBucket.set(bucketYear, p);
              return;
            }
            if (isFY(existingLabel) && !isFY(newLabel)) {
              return;
            }

            // If both are the same type, keep the one with the latest timestamp
            if (toEpoch(p.timestamp) > toEpoch(existing.timestamp)) {
              perBucket.set(bucketYear, p);
            }
          });

          displayArr = Array.from(perBucket.values()).sort((a, b) => toEpoch(a.timestamp) - toEpoch(b.timestamp));
        }

        // Populate bucketed year -> label map and track earliest timestamp per year
        displayArr.forEach(p => {
          if (!p.displayLabel) return;
          if (opts.period === 'annual') {
            const bucketYear = getBucketYearFromEvent(p);
            if (bucketYear == null) return;
            const x = Date.UTC(bucketYear, 0, 1);
            const key = String(bucketYear);
            timestampToDisplayLabel.set(x, key);
            allDisplayLabels.add(key);
            const existing = labelToMinTimestamp.get(key);
            if (existing === undefined || x < existing) {
              labelToMinTimestamp.set(key, x);
            }
          } else {
            const t = toEpoch(p.timestamp);
            timestampToDisplayLabel.set(t, p.displayLabel);
            allDisplayLabels.add(p.displayLabel);
            const existing = labelToMinTimestamp.get(p.displayLabel);
            if (existing === undefined || t < existing) {
              labelToMinTimestamp.set(p.displayLabel, t);
            }
          }
        });

        const vals = displayArr.map(p => (typeof p.value === 'number' ? (p.value as number) : NaN)).filter(v => Number.isFinite(v));
        const yAxisIndex = guessAxisIndex(ek, vals);
        const isRatio = yAxisIndex === 1;
        console.log(`[Multi-Company ${companySymbol}] Processing "${ek}": displayArr.length=${displayArr.length}, yAxisIndex=${yAxisIndex}, isRatio=${isRatio}, isRatioMetricName=${isRatioMetricName(ek)}`);

        // Update max time if this company extends further (respect bucket year in annual mode)
        const lastTs = displayArr
          .filter(p => typeof p.value === 'number')
          .map(p => {
            if (opts.period === 'annual' && p.displayLabel) {
              const by = getBucketYearFromEvent(p);
              return by != null ? Date.UTC(by, 0, 1) : toEpoch(p.timestamp);
            }
            return toEpoch(p.timestamp);
          })
          .reduce((max, t) => (t > max ? t : max), 0);
        if (lastTs > eventsFirstKeyLastMs) eventsFirstKeyLastMs = lastTs;

        // Get color from pairColorMap if available
        const pairKey = `${companySymbol}-${normalizeKey(ek)}`;
        const pairColor = pairColorMap.get(pairKey) || columnPalette[(companyIdx + colIdx) % columnPalette.length];

        if (isRatio) {
          // Ratio metrics as lines - add directly to series
          const data = displayArr.map(p => ({ x: toEpoch(p.timestamp), y: typeof p.value === 'number' ? p.value : null }));
          const lineData = data.map(p => [p.x, p.y] as [number, number | null]);

          // Growth metrics should be smooth lines, not stepped
          const isGrowthMetric = normalizeKey(ek).includes('GROWTH') || normalizeKey(ek).includes('_GROWTH');

          series.push({
            type: 'line',
            name: `${companySymbol} - ${formatMetricName(ek)}`,
            data: lineData as any,
            yAxis: 1,
            color: pairColor,
            lineWidth: 1.8,
            marker: { enabled: false },
            connectNulls: true,
            step: isGrowthMetric ? undefined : 'left', // Only step for non-growth metrics
            dataGrouping: { enabled: true }
          } as Highcharts.SeriesLineOptions);
        } else {
          // Amount metrics as columns - add to pendingColumns for category mode processing
          hasEventColumns = true;

          // For annual, rewrite timestamps in displayArr to bucket-year start-of-year dates so
          // all companies' FY/TTM values share the same calendar-year x position.
          const bucketedDisplayArr = displayArr.map(p => {
            if (opts.period === 'annual' && p.displayLabel) {
              const by = getBucketYearFromEvent(p);
              if (by != null) {
                return { ...p, timestamp: new Date(Date.UTC(by, 0, 1)).toISOString().slice(0, 10) };
              }
            }
            return p;
          });

          pendingColumns.push({
            key: ek,
            displayArr: bucketedDisplayArr,
            yAxisIndex,
            color: pairColor,
            companySymbol
          });
        }
      });
      companyIdx++;
    });
  }

  // Process Comparison API Events (legacy)
  if (opts.comparisonApi && opts.comparisonApi.events) {
    const compEvents = opts.comparisonApi.events;
    // Get date range for comparison from its metadata
    const compRequestFromMs = opts.comparisonApi.metadata?.requestFrom ? toEpoch(opts.comparisonApi.metadata.requestFrom) : 0;
    const compRequestFromYear = opts.comparisonApi.metadata?.requestFrom
      ? new Date(`${opts.comparisonApi.metadata.requestFrom}T00:00:00Z`).getUTCFullYear()
      : undefined;

    Object.keys(compEvents).forEach((ek) => {
      if (!matchesFilter(normalizeKey(ek))) return;
      const arr = compEvents[ek] || [];
      if (!Array.isArray(arr) || arr.length === 0) return;

      // For annual view, keep only FY and TTM entries, filter out quarterly
      // Also filter by requestFrom **year** from metadata (e.g. keep FY >= 2020), always include TTM
      let displayArr: Array<{ timestamp: string; value: number | null; displayLabel?: string }> = arr;
      if (opts.period === 'annual') {
        const perLabel = new Map<string, { timestamp: string; value: number | null; displayLabel?: string }>();
        arr.forEach(p => {
          const t = toEpoch(p.timestamp);
          const isTTM = p.displayLabel?.toUpperCase() === 'TTM';
          let shouldKeep = false;
          let key = '';

          if (p.displayLabel) {
            const label = p.displayLabel.toUpperCase();
            shouldKeep = label.startsWith('FY') || label === 'TTM';
            key = p.displayLabel;
          } else if (p.period) {
            const period = p.period.toUpperCase();
            shouldKeep = period === 'FY';
            key = p.fiscalYear ? `FY'${String(p.fiscalYear).slice(-2)}` : p.timestamp;
          }

          if (!shouldKeep) return;

          // Additional year-based filtering for non-TTM entries in annual mode
          if (!isTTM && compRequestFromYear !== undefined) {
            const fy = getFiscalYearFromEvent(p);
            if (fy != null) {
              if (fy < compRequestFromYear) return;
            } else if (compRequestFromMs > 0 && t < compRequestFromMs) {
              return;
            }
          }

          const existing = perLabel.get(key);
          if (!existing || t > toEpoch(existing.timestamp)) {
            perLabel.set(key, { timestamp: p.timestamp, value: extractEventValue(p), displayLabel: p.displayLabel || key, formattedValue: p.formattedValue });
          }
        });
        displayArr = Array.from(perLabel.values()).sort((a, b) => toEpoch(a.timestamp) - toEpoch(b.timestamp));
      }

      // Populate timestamp -> displayLabel map and track earliest real timestamp per label
      displayArr.forEach(p => {
        if (p.displayLabel) {
          const t = toEpoch(p.timestamp);
          timestampToDisplayLabel.set(t, p.displayLabel);
          const existing = labelToMinTimestamp.get(p.displayLabel);
          if (existing === undefined || t < existing) {
            labelToMinTimestamp.set(p.displayLabel, t);
          }
        }
      });

      const vals = displayArr.map(p => (typeof p.value === 'number' ? (p.value as number) : NaN)).filter(v => Number.isFinite(v));
      const yAxisIndex = guessAxisIndex(ek, vals);
      const data = displayArr.map(p => ({ x: toEpoch(p.timestamp), y: typeof p.value === 'number' ? p.value : null, formattedValue: p.formattedValue }));

      // Update max time if comparison extends further
      const lastTs = displayArr.map(p => toEpoch(p.timestamp)).reduce((max, t) => (t > max ? t : max), 0);
      if (lastTs > eventsFirstKeyLastMs) eventsFirstKeyLastMs = lastTs;

      series.push({
        type: 'column',
        name: formatMetricName(ek) + ' (Comp)',
        data: data as any,
        yAxis: yAxisIndex,
        color: '#9CA3AF', // Gray for comparison
        pointRange: prMs,
        borderWidth: 0,
        pointPadding: 0.2, // Offset slightly or make narrower
        pointPlacement: 0.15, // Shift to right
        opacity: 0.7,
        dataLabels: {
          enabled: true, // Enable for all periods
          style: { color: '#9CA3AF', textOutline: 'none', fontSize: '9px' },
          formatter: function () {
            const point: any = this.point;
            // Use formattedValue from API if available
            if (point && point.formattedValue) {
              return point.formattedValue;
            }
            const y = this.y as number | null;
            if (y == null) return '';
            return bigNumberLabel(y);
          },
          y: -5
        },
        states: { hover: { enabled: true, brightness: 0.1 } },
        zIndex: 0, // Behind primary
        dataGrouping: { enabled: false }
      } as Highcharts.SeriesColumnOptions);
    });
  }

  // Finalize event columns
  // Sort displayLabels in chronological order.
  // - Annual: by fiscal year (FY'20, FY'21, ..., TTM)
  // - Quarterly: by (year, quarter) across labels (Q1'20, Q2'20, Q3'20, Q4'20, Q1'21, ...)
  const sortedCategories = Array.from(allDisplayLabels).sort((a, b) => {
    if (a === 'TTM') return 1;
    if (b === 'TTM') return -1;

    // In quarterly mode, prefer fiscal (year, quarter) ordering based on labels
    if (opts.period === 'quarter') {
      const qa = parseQuarterFromLabel(a);
      const qb = parseQuarterFromLabel(b);
      if (qa && qb) {
        if (qa.year !== qb.year) return qa.year - qb.year;
        if (qa.quarter !== qb.quarter) return qa.quarter - qb.quarter;
      }
    }

    // Otherwise, fall back to earliest real timestamp if available
    const ta = labelToMinTimestamp.get(a);
    const tb = labelToMinTimestamp.get(b);
    if (ta !== undefined && tb !== undefined && ta !== tb) {
      return ta - tb;
    }

    const yearA = parseFiscalYearFromLabel(a) ?? 0;
    const yearB = parseFiscalYearFromLabel(b) ?? 0;
    if (yearA !== yearB) return yearA - yearB;

    return a.localeCompare(b);
  });

  // Map each displayLabel to a synthetic but ordered fiscal timestamp.
  // This lets us keep a datetime x-axis while grouping bars by fiscal period.
  const labelToFiscalTimestamp = new Map<string, number>(); // kept for potential future use, but real timestamps drive the axis

  let eventTickPositions: number[] | undefined = undefined;
  // We keep a standard datetime axis but drive tick positions from the
  // real event timestamps, deduplicated by fiscal displayLabel, so that
  // FY/TTM labels appear once and daily P/E lines can still use their
  // true timestamps.
  const useFiscalAxis = false;

  if (pendingColumns.length > 0) {
    // Collect all unique timestamps across all pending columns for tick positions
    const allTimestamps = new Set<number>();

    pendingColumns.forEach(pc => {
      const data = pc.displayArr.map(p => {
        const x = toEpoch(p.timestamp);
        if (p.displayLabel) {
          // Map the real event timestamp to its fiscal label for x-axis
          // and tooltip display.
          timestampToDisplayLabel.set(x, p.displayLabel);
        }
        allTimestamps.add(x);
        return { x, y: typeof p.value === 'number' ? p.value : null, formattedValue: p.formattedValue };
      }).sort((a, b) => a.x - b.x); // Ensure data is sorted chronologically

      // Include company symbol in name if we have multiple companies
      const seriesName = pc.companySymbol
        ? `${pc.companySymbol} - ${formatMetricName(pc.key)}`
        : formatMetricName(pc.key);

      series.push({
        type: 'column',
        name: seriesName,
        data: data as any,
        yAxis: pc.yAxisIndex,
        color: pc.color,
        pointRange: prMs,
        pointPadding: 0.1,
        groupPadding: 0.1,
        borderWidth: 0,
        dataLabels: {
          enabled: true, // Enable for all periods (annual and quarterly)
          style: { color: 'var(--text-primary)', textOutline: 'none', fontSize: '10px', fontWeight: '600' },
          formatter: function () {
            const point: any = this.point;
            // Use formattedValue from API if available, otherwise calculate it
            if (point && point.formattedValue) {
              return point.formattedValue;
            }
            const y = this.y as number | null;
            if (y == null) return '';
            return bigNumberLabel(y);
          },
          y: -5 // Position labels slightly above bars
        },
        states: { hover: { enabled: true, brightness: 0.05 } },
        zIndex: 1,
        dataGrouping: { enabled: false }
      } as Highcharts.SeriesColumnOptions);
    });

    const xs = Array.from(allTimestamps).sort((a, b) => a - b);

    // Deduplicate tick positions by fiscal label when available so multi-company
    // bars for the same FY/TTM share a single x-axis tick. Fall back to the
    // raw timestamps when no labels are present.
    if (labelToMinTimestamp.size > 0) {
      eventTickPositions = Array.from(labelToMinTimestamp.values()).sort((a, b) => a - b);
    } else if (opts.period === 'quarter') {
      const byLabel = new Map<string, number>();
      xs.forEach(t => {
        const lbl = timestampToDisplayLabel.get(t);
        const key = lbl ?? `__NO_LABEL__${t}`;
        if (!byLabel.has(key)) {
          byLabel.set(key, t);
        }
      });
      eventTickPositions = Array.from(byLabel.values()).sort((a, b) => a - b);
    } else {
      eventTickPositions = xs;
    }
  }

  // Decide x-axis cap
  // Always display data until the latest available point across all series
  let xAxisMaxMs: number | undefined = undefined;
  if (hasEventColumns && eventsFirstKeyLastMs > 0) {
    // Start with events' last timestamp (bars/columns)
    xAxisMaxMs = eventsFirstKeyLastMs;
  }

  // Check if dailyRaw (line charts) extends beyond events
  if (dailyRaw.length > 0) {
    const lastDaily = dailyRaw[dailyRaw.length - 1];
    const lastDailyMs = lastDaily ? toEpoch(String(lastDaily.timestamp)) : 0;

    // Use the later of events or daily data as the x-axis max
    // This ensures we render all data (both bars and lines) until data is available
    if (!xAxisMaxMs || lastDailyMs > xAxisMaxMs) {
      xAxisMaxMs = lastDailyMs;
    }
  }

  // For annual charts with event columns, extend ratio line series (yAxis index 1)
  // back to the first event bucket year so the line visually covers the full
  // period even if the first real data point is later than that year.
  if (opts.period === 'annual' && hasEventColumns && eventTickPositions && eventTickPositions.length > 0) {
    const firstBucketTs = eventTickPositions[0];
    series.forEach((s, idx) => {
      if (!s || s.type !== 'line') return;
      const line = s as Highcharts.SeriesLineOptions;
      const yAxisIndex = (line as any).yAxis as number | undefined;
      if (yAxisIndex !== 1) return;

      const dataAny: any[] = (line.data as any[]) || [];
      if (!Array.isArray(dataAny) || dataAny.length === 0) return;

      // Find the first point with a defined numeric x and non-null y
      let firstIdx = -1;
      for (let i = 0; i < dataAny.length; i++) {
        const pt = dataAny[i];
        const x = Array.isArray(pt) ? pt[0] : (pt && typeof pt.x === 'number' ? pt.x : undefined);
        const y = Array.isArray(pt) ? pt[1] : (pt ? pt.y : undefined);
        if (typeof x === 'number' && y != null) {
          firstIdx = i;
          break;
        }
      }
      if (firstIdx === -1) return;

      const firstPt = dataAny[firstIdx];
      const firstX = Array.isArray(firstPt) ? firstPt[0] : firstPt.x;
      const firstY = Array.isArray(firstPt) ? firstPt[1] : firstPt.y;

      if (typeof firstX !== 'number' || firstY == null) return;
      if (firstBucketTs >= firstX) return; // already starting at or before first bucket

      const newPt = Array.isArray(firstPt)
        ? [firstBucketTs, firstY]
        : { x: firstBucketTs, y: firstY };

      dataAny.unshift(newPt);
      line.data = dataAny as any;
      series[idx] = line as any;
    });
  }

  const options: Highcharts.Options = {
    chart: {
      backgroundColor: 'transparent',
      style: { fontFamily: 'Inter, sans-serif', color: 'var(--text-primary)' },
      height: opts.height ?? 520
    },
    time: { useUTC: true } as any,
    credits: {
      enabled: true,
      text: 'Powered by StockFortress',
      href: 'https://stockfortress.com',
      style: { color: 'rgba(255, 255, 255, 0.4)', fontSize: '10px' }
    },
    rangeSelector: {
      enabled: false
    },
    navigator: {
      enabled: false
    },
    scrollbar: {
      enabled: false
    },
    xAxis: [{
      type: 'datetime',
      // Use ordinal axis only when we have event columns AND no daily line data
      // Daily line data (like P/E ratio) needs a continuous datetime axis for proper rendering
      ordinal: hasEventColumns && !hasDailyLineData,
      crosshair: true,
      gridLineColor: '#E5E7EB', // Modern light gray
      lineColor: '#374151',
      tickColor: '#374151',
      endOnTick: false,
      startOnTick: false,
      // Add padding when we have daily line data extending beyond event columns
      maxPadding: hasDailyLineData ? 0.02 : 0,
      minPadding: 0,
      // Disable automatic tick generation when we have event columns
      tickPixelInterval: (hasEventColumns && eventTickPositions && eventTickPositions.length > 0) ? 999999 : undefined,
      // Disable automatic date/time label formatting when we have event columns
      dateTimeLabelFormats: hasEventColumns ? {
        millisecond: '',
        second: '',
        minute: '',
        hour: '',
        day: '',
        week: '',
        month: '',
        year: ''
      } : undefined,
      // Clamp visible range to the earliest/latest fiscal/event ticks when available
      // so that all FY/TTM labels (e.g. FY'20) are visible when their columns exist.
      min: (() => {
        if (eventTickPositions && eventTickPositions.length > 0) {
          return eventTickPositions[0];
        }
        if (api.metadata?.requestFrom) {
          const t = new Date(api.metadata.requestFrom!).getTime();
          return isNaN(t) ? undefined : t;
        }
        return undefined;
      })(),
      max: (() => {
        // Use the maximum of event tick positions and daily line data (xAxisMaxMs)
        // so that line charts extending beyond the last event bar are fully visible
        const eventMax = (eventTickPositions && eventTickPositions.length > 0)
          ? eventTickPositions[eventTickPositions.length - 1]
          : undefined;
        const dailyMax = (xAxisMaxMs && xAxisMaxMs > 0) ? xAxisMaxMs : undefined;

        if (eventMax && dailyMax) {
          return Math.max(eventMax, dailyMax);
        }
        if (eventMax) return eventMax;
        if (dailyMax) return dailyMax;

        const reqTo = api.metadata?.requestTo ? new Date(api.metadata.requestTo!).getTime() : undefined;
        return reqTo;
      })(),
      labels: {
        style: {
          color: 'var(--text-secondary)',
          fontSize: '11px'
        },
        // Rotate labels when there are many data points to prevent overlap
        rotation: (() => {
          const numLabels = eventTickPositions?.length || 0;
          if (opts.period === 'quarter') {
            // For quarterly: rotate based on number of labels
            if (numLabels > 30) return -90; // Vertical for very crowded charts (7+ years)
            if (numLabels > 20) return -45; // Diagonal for moderately crowded charts (5+ years)
            return 0; // Horizontal for reasonable number of quarters (up to 5 years)
          }
          // For annual: only rotate if we have many years
          return numLabels > 15 ? -45 : 0;
        })(),
        // Allow labels to overflow and prevent automatic hiding
        overflow: 'allow' as any,
        // Adjust alignment based on rotation
        align: (() => {
          const numLabels = eventTickPositions?.length || 0;
          if (opts.period === 'quarter' && numLabels > 20) {
            return 'right' as any; // Right align for rotated quarterly labels
          }
          return 'center' as any;
        })(),
        y: (() => {
          const numLabels = eventTickPositions?.length || 0;
          if (opts.period === 'quarter' && numLabels > 30) {
            return 10; // More offset for vertically rotated labels
          }
          if (opts.period === 'quarter' && numLabels > 20) {
            return 15; // More offset for diagonally rotated labels
          }
          return 20; // Default offset to keep labels lower
        })(),
        formatter: function () {
          const v = Number(this.value);
          if (!Number.isFinite(v)) return '';

          let labelText = '';
          const displayLabel = timestampToDisplayLabel.get(v);

          if (hasEventColumns) {
            if (opts.period === 'annual') {
              // In annual event mode, always show the bucket year (calendar year)
              const date = new Date(v);
              const year = date.getUTCFullYear();
              labelText = year.toString();
            } else {
              // In quarterly event mode, prefer the fiscal displayLabel when present,
              // otherwise fall back to a derived quarter label.
              if (displayLabel) {
                labelText = displayLabel;
              } else {
                const date = new Date(v);
                const year = date.getUTCFullYear();
                const month = date.getUTCMonth(); // 0-11
                const quarter = Math.floor(month / 3) + 1; // 1-4
                const shortYear = year.toString().slice(-2); // Last 2 digits
                labelText = `Q${quarter}'${shortYear}`;
              }
            }
          } else {
            // When there are no event columns, derive labels directly from timestamps.
            const date = new Date(v);
            const year = date.getUTCFullYear();
            const month = date.getUTCMonth(); // 0-11

            if (opts.period === 'annual') {
              labelText = year.toString();
            } else {
              const quarter = Math.floor(month / 3) + 1; // 1-4
              const shortYear = year.toString().slice(-2); // Last 2 digits
              labelText = `Q${quarter}'${shortYear}`;
            }
          }

          console.log('[xAxis label]', {
            value: v,
            date: new Date(v).toISOString(),
            displayLabel,
            hasEventColumns,
            labelText
          });

          return labelText;
        }
      },
      minorTicks: false,
      minorTickInterval: undefined,
      // When we have event columns, use explicit tickPositions array
      // This is more reliable than tickPositioner for preventing auto-generated ticks
      // Use explicit tick positions for event data when we have a reasonable number
      // For quarterly: allow up to 40 quarters (~10 years)
      // For annual: allow up to 20 years
      tickPositions: (hasEventColumns && eventTickPositions && eventTickPositions.length > 0 &&
        ((opts.period === 'quarter' && eventTickPositions.length <= 40) ||
          (opts.period === 'annual' && eventTickPositions.length <= 20)))
        ? eventTickPositions
        : undefined,
      // Disable Highcharts Stock's automatic units when we're using explicit tick positions
      units: (hasEventColumns && eventTickPositions && eventTickPositions.length > 0 &&
        ((opts.period === 'quarter' && eventTickPositions.length <= 40) ||
          (opts.period === 'annual' && eventTickPositions.length <= 20)))
        ? [[null, null] as any] // Disable all automatic units
        : undefined,
      // Only use tickPositioner when we're not using explicit positions
      tickPositioner: (hasEventColumns && eventTickPositions && eventTickPositions.length > 0 &&
        ((opts.period === 'quarter' && eventTickPositions.length <= 40) ||
          (opts.period === 'annual' && eventTickPositions.length <= 20)))
        ? undefined
        : (opts.period === 'annual')
          ? function () {
            const axis: any = this as any;
            const min = axis.min as number;
            const maxEdge = (xAxisMaxMs && xAxisMaxMs > 0) ? xAxisMaxMs : (axis.max as number);
            if (!Number.isFinite(min) || !Number.isFinite(maxEdge)) return axis.tickPositions || [];
            const positions: number[] = [];
            const minYear = new Date(min).getUTCFullYear();
            const maxYear = new Date(maxEdge).getUTCFullYear();
            for (let y = minYear; y <= maxYear; y++) {
              const t = Date.UTC(y, 0, 1);
              if (t >= min && t <= maxEdge) positions.push(t);
            }
            return positions.length ? positions : (axis.tickPositions || []);
          }
          : undefined,

    }],
    yAxis: yAxes,
    legend: {
      enabled: false
    },
    tooltip: {
      shared: true,
      backgroundColor: '#1F2937',
      borderColor: '#374151',
      borderWidth: 1,
      borderRadius: 6,
      shadow: true,
      style: {
        color: '#FFFFFF',
        fontSize: '12px'
      },
      formatter: function (this: Highcharts.TooltipFormatterContextObject) {
        const ctx: any = this as any;
        const points: Highcharts.Point[] = ctx.points || (ctx.point ? [ctx.point] : []);
        const x = Number(ctx.x);

        // Use displayLabel as header when available (FY'20, Q2'05, TTM, etc.)
        const headerLabel = (Number.isFinite(x) && timestampToDisplayLabel.get(x))
          ? timestampToDisplayLabel.get(x)
          : (Number.isFinite(x) ? Highcharts.dateFormat('%Y-%m-%d', x) : '');

        let html = '';
        if (headerLabel) {
          html += `<span style="font-size:11px;opacity:0.85">${headerLabel}</span><br/>`;
        }

        points.forEach((p) => {
          const series = p.series;
          const yAxisIndex = (series as any).yAxis?.index ?? 0;
          const val = p.y as number | null;
          if (val === null || val === undefined) return;

          let txt = '';
          if (yAxisIndex === 0) txt = `$${Highcharts.numberFormat(val, 2)} `;
          else if (yAxisIndex === 1) txt = Highcharts.numberFormat(val, 2);
          else txt = bigNumberLabel(val);

          html += `<span style="color:${p.color}">${series.name}</span>: <b style="color:#FFFFFF">${txt}</b><br/>`;
        });

        return html;
      }
    },
    plotOptions: {
      column: {
        pointPadding: 0.05,
        groupPadding: 0.12,
        borderRadius: 3
      },
      series: {
        dataGrouping: { enabled: false },
        turboThreshold: 100000,
        zIndex: 2
      }
    },
    series
  };

  console.log(`[buildHighchartsOptions] Final series count: ${series.length}`, series.map(s => ({ name: (s as any).name, type: s.type, dataLength: ((s as any).data || []).length })));
  console.log(`[buildHighchartsOptions] hasEventColumns=${hasEventColumns}, eventTickPositions=`, eventTickPositions?.map(t => ({ ts: t, date: new Date(t).toISOString(), label: timestampToDisplayLabel.get(t) })));
  return options;
}


const FinancialChart: FC<Props> = (props) => {
  const { symbol: propSymbol, symbols, comparisonSymbol, metrics: propMetrics, companyMetricPairs, period, includePrice = false, height, from, to } = props;

  // Derive symbol and metrics from companyMetricPairs if provided
  // For multi-company support, we need to group metrics by company
  const uniqueCompanies = companyMetricPairs
    ? Array.from(new Set(companyMetricPairs.map(p => p.company)))
    : (symbols || (propSymbol ? [propSymbol] : []));

  const symbol = propSymbol || (uniqueCompanies.length > 0 ? uniqueCompanies[0] : '');
  const metrics = propMetrics || (companyMetricPairs ? companyMetricPairs.map(p => p.metric.metricAttributeName || '') : []);

  // For multi-company, create a map of company -> metrics
  const companyMetricsMap = useMemo(() => {
    if (!companyMetricPairs) return new Map<string, string[]>();
    const map = new Map<string, string[]>();
    companyMetricPairs.forEach(pair => {
      const existing = map.get(pair.company) || [];
      const metricName = pair.metric.metricAttributeName || '';
      if (metricName && !existing.includes(metricName)) {
        existing.push(metricName);
      }
      map.set(pair.company, existing);
    });
    return map;
  }, [companyMetricPairs]);

  const [data, setData] = useState<ApiResponse | null>(null);
  const [multiCompanyData, setMultiCompanyData] = useState<Map<string, ApiResponse>>(new Map());
  const [comparisonData, setComparisonData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [internalFrom, setInternalFrom] = useState<string | undefined>(from);
  const [internalTo, setInternalTo] = useState<string | undefined>(to);

  // Range slider state
  const [sliderMin, setSliderMin] = useState(0);
  const [sliderMax, setSliderMax] = useState(100);

  // Temporary slider state during drag (before release)
  const [tempSliderMin, setTempSliderMin] = useState(0);
  const [tempSliderMax, setTempSliderMax] = useState(100);
  const [isDragging, setIsDragging] = useState(false);

  // Preserve the original full financial date range (never changes after first load)
  const [fullFinancialRange, setFullFinancialRange] = useState<{ min: string, max: string } | null>(null);

  // Sync props to internal state when they change
  useEffect(() => {
    setInternalFrom(from);
    setInternalTo(to);
  }, [from, to]);

  // Fetch data for all companies when using companyMetricPairs
  useEffect(() => {
    // Skip if not using companyMetricPairs
    if (!companyMetricPairs || companyMetricPairs.length === 0) {
      return;
    }

    // Don't make API call if no metrics are selected
    const allMetrics = Array.from(companyMetricsMap.values()).flat();
    if (companyMetricsMap.size === 0 || allMetrics.length === 0) {
      console.log('Skipping data fetch - no metrics selected');
      setData(null);
      setMultiCompanyData(new Map());
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    console.log('Data fetch triggered:', { companies: Array.from(companyMetricsMap.keys()), period, companyMetricsMap: Object.fromEntries(companyMetricsMap), includePrice, internalFrom, internalTo });

    (async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch data for each company
        const fetchPromises = Array.from(companyMetricsMap.entries()).map(async ([company, companyMetrics]) => {
          console.log(`[API Call] Fetching for ${company} with metrics:`, companyMetrics);
          const resp = await StockService.getChartTimeSeries({
            symbol: company,
            period,
            metrics: companyMetrics,
            includePrice: false, // Don't include price data
            from: internalFrom,
            to: internalTo
          });
          console.log(`[API Response] ${company}:`, {
            chartPoints: resp?.chartPoints?.length,
            chartPointsSample: resp?.chartPoints?.slice(0, 2),
            events: resp?.events ? Object.keys(resp.events) : [],
            series: resp?.series?.length
          });
          return { company, data: resp as ApiResponse };
        });

        const results = await Promise.all(fetchPromises);

        if (!cancelled) {
          const newMap = new Map<string, ApiResponse>();
          results.forEach(({ company, data }) => {
            newMap.set(company, data);
          });
          setMultiCompanyData(newMap);
          // Set first company's data as primary for backwards compatibility
          if (results.length > 0) {
            setData(results[0].data);
          }
        }
      } catch (e: any) {
        if (!cancelled) setError('Failed to load chart');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyMetricPairs, JSON.stringify(Array.from(companyMetricsMap.entries())), period, includePrice, internalFrom, internalTo]);

  // Legacy single-symbol fetch (when not using companyMetricPairs)
  useEffect(() => {
    // Skip if using companyMetricPairs
    if (companyMetricPairs && companyMetricPairs.length > 0) return;

    // Don't make API call if no metrics are selected
    if (!propMetrics || propMetrics.length === 0) {
      console.log('Skipping data fetch - no metrics selected');
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    console.log('Data fetch triggered:', { symbol, period, metrics: propMetrics, includePrice, internalFrom, internalTo });

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const resp = await StockService.getChartTimeSeries({
          symbol,
          period,
          metrics: propMetrics,
          includePrice: false, // Don't include price data
          from: internalFrom,
          to: internalTo
        });
        if (!cancelled) setData(resp as ApiResponse);
      } catch (e: any) {
        if (!cancelled) setError('Failed to load chart');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [symbol, JSON.stringify(propMetrics), period, includePrice, internalFrom, internalTo, companyMetricPairs]);

  // Fetch Comparison Data
  useEffect(() => {
    if (!comparisonSymbol || !metrics || metrics.length === 0) {
      setComparisonData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Don't set main loading state for comparison to avoid flickering existing chart
        const resp = await StockService.getChartTimeSeries({
          symbol: comparisonSymbol,
          period,
          metrics,
          includePrice: false, // Don't include price data
          from: internalFrom,
          to: internalTo
        });
        if (!cancelled) setComparisonData(resp as ApiResponse);
      } catch (e: any) {
        console.error('Failed to load comparison chart', e);
      }
    })();
    return () => { cancelled = true; };
  }, [comparisonSymbol, JSON.stringify(metrics), period, includePrice, internalFrom, internalTo]);

  const options = useMemo(
    () => data ? buildHighchartsOptions(data, {
      period,
      includePrice,
      height,
      metricsFilter: metrics,
      comparisonApi: comparisonData,
      multiCompanyData,
      companyMetricPairs
    }) : undefined,
    [data, comparisonData, multiCompanyData, companyMetricPairs, period, includePrice, height, metrics]
  );

  // Update slider visual position during drag
  const handleSliderDrag = (min: number, max: number) => {
    setTempSliderMin(min);
    setTempSliderMax(max);
  };

  // Commit slider change and trigger API call on release
  const handleSliderRelease = () => {
    const min = tempSliderMin;
    const max = tempSliderMax;

    // Always commit the visual slider position so the thumb doesn't snap back
    setIsDragging(false);
    setSliderMin(min);
    setSliderMax(max);

    // If we don't yet know the full financial range, skip the API call
    if (!fullFinancialRange) return;

    const financialMinMs = new Date(fullFinancialRange.min).getTime();
    const financialMaxMs = new Date(fullFinancialRange.max).getTime();

    // Validate timestamps
    if (isNaN(financialMinMs) || isNaN(financialMaxMs)) {
      console.error('Invalid date range:', fullFinancialRange.min, fullFinancialRange.max);
      return;
    }

    const totalMs = financialMaxMs - financialMinMs;

    const fromMs = financialMinMs + (totalMs * min / 100);
    const toMs = financialMinMs + (totalMs * max / 100);


    const newFrom = new Date(fromMs).toISOString().split('T')[0];
    const newTo = new Date(toMs).toISOString().split('T')[0];

    console.log('Slider released:', {
      sliderPercent: { min, max },
      calculatedDates: { newFrom, newTo },
      currentDates: { internalFrom, internalTo },
      fullRange: fullFinancialRange
    });

    // Trigger API call with new dates
    setInternalFrom(newFrom);
    setInternalTo(newTo);
  };

  // Initialize and update slider positions based on loaded data and current request range
  useEffect(() => {
    if (!data?.metadata || !data.metadata.financialMinDate || !data.metadata.financialMaxDate) return;

    // On first load, preserve the full financial range
    if (!fullFinancialRange) {
      setFullFinancialRange({
        min: data.metadata.financialMinDate,
        max: data.metadata.financialMaxDate
      });
    }

    const rangeToUse = fullFinancialRange || {
      min: data.metadata.financialMinDate,
      max: data.metadata.financialMaxDate
    };

    // If caller didn't specify from/to (e.g. when using the "All" range),
    // snap internalFrom/internalTo to the full financial range so that
    // subsequent API calls use the true min/max dates returned by the backend
    // instead of the backend's default window.
    if (!internalFrom && !internalTo) {
      const fullMin = rangeToUse.min;
      const fullMax = rangeToUse.max;
      if (fullMin && fullMax) {
        let changed = false;
        if (internalFrom !== fullMin) {
          setInternalFrom(fullMin);
          changed = true;
        }
        if (internalTo !== fullMax) {
          setInternalTo(fullMax);
          changed = true;
        }
        // Let the effect re-run with updated internalFrom/internalTo
        if (changed) return;
      }
    }

    const financialMinMs = new Date(rangeToUse.min).getTime();
    const financialMaxMs = new Date(rangeToUse.max).getTime();

    // Prefer the internally requested range (from slider), fall back to metadata
    const fromStr = internalFrom || data.metadata.requestFrom;
    const toStr = internalTo || data.metadata.requestTo;
    if (!fromStr || !toStr) return;

    const requestFromMs = new Date(fromStr).getTime();
    const requestToMs = new Date(toStr).getTime();

    // Validate all timestamps
    if (isNaN(financialMinMs) || isNaN(financialMaxMs) || isNaN(requestFromMs) || isNaN(requestToMs)) {
      console.error('Invalid dates in slider init:', { rangeToUse, fromStr, toStr });
      return;
    }

    const totalMs = financialMaxMs - financialMinMs;
    if (totalMs <= 0) {
      console.error('Invalid date range: min >= max');
      return;
    }

    const currentFromMs = requestFromMs - financialMinMs;
    const currentToMs = requestToMs - financialMinMs;

    const minPercent = (currentFromMs / totalMs) * 100;
    const maxPercent = (currentToMs / totalMs) * 100;

    setSliderMin(minPercent);
    setSliderMax(maxPercent);
    setTempSliderMin(minPercent);
    setTempSliderMax(maxPercent);
  }, [data?.metadata, fullFinancialRange, internalFrom, internalTo]);



  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 bg-theme-secondary text-theme-primary">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
        <span className="ml-3">Loading chart...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg bg-red-900/20 border border-red-700 m-4 text-red-300">
        {error}
      </div>
    );
  }

  // Show message when no metrics are selected
  if (!metrics || metrics.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 bg-theme-secondary text-theme-secondary rounded-lg m-4">
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-theme-tertiary mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="text-sm">Select a metric to view the chart</p>
        </div>
      </div>
    );
  }

  if (!options) return null;

  return (
    <div className="bg-theme-secondary rounded-lg p-2">
      <HighchartsReact highcharts={Highcharts as any} constructorType="stockChart" options={options} />

      {/* Custom Range Slider (temporarily disabled in favor of 1Y/3Y/5Y/All presets) */}
      {false && data?.metadata && (
        <div className="mt-6 px-4">
          <div className="relative h-10">
            {/* Timeline labels */}
            <div className="absolute -top-5 left-0 right-0 flex justify-between text-xs text-theme-secondary">
              {(() => {
                if (!fullFinancialRange) return null;

                const minYear = parseInt(fullFinancialRange.min.split('-')[0] || '2000');
                const maxYear = parseInt(fullFinancialRange.max.split('-')[0] || '2025');
                const range = maxYear - minYear;
                const numLabels = 7; // Show 7 labels evenly spaced

                return Array.from({ length: numLabels }).map((_, i) => {
                  const year = minYear + Math.round((range * i) / (numLabels - 1));
                  const isSelected =
                    data.metadata?.requestFrom &&
                    data.metadata?.requestTo &&
                    year >= parseInt(data.metadata.requestFrom.split('-')[0]) &&
                    year <= parseInt(data.metadata.requestTo.split('-')[0]);

                  return (
                    <span
                      key={i}
                      className={isSelected ? 'font-bold text-green-400' : ''}
                    >
                      {year}
                    </span>
                  );
                });
              })()}
            </div>

            {/* Slider track */}
            <div
              className="absolute top-3 left-0 right-0 h-1 bg-gray-700/90 rounded-full cursor-pointer"
              onClick={(e) => {
                if (!data?.metadata) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const clickX = e.clientX - rect.left;
                const percent = (clickX / rect.width) * 100;

                // Move the closest handle to the click position
                const distToMin = Math.abs(percent - (isDragging ? tempSliderMin : sliderMin));
                const distToMax = Math.abs(percent - (isDragging ? tempSliderMax : sliderMax));

                if (distToMin < distToMax) {
                  setTempSliderMin(Math.max(0, Math.min(percent, (isDragging ? tempSliderMax : sliderMax) - 1)));
                  setTempSliderMax(isDragging ? tempSliderMax : sliderMax);
                } else {
                  setTempSliderMin(isDragging ? tempSliderMin : sliderMin);
                  setTempSliderMax(Math.min(100, Math.max(percent, (isDragging ? tempSliderMin : sliderMin) + 1)));
                }

                // Immediately commit the change
                setTimeout(() => handleSliderRelease(), 0);
              }}
            >
              {/* Green highlighted section */}
              <div
                className="absolute h-full bg-green-500 rounded-full pointer-events-none transition-all duration-150 ease-out"
                style={{
                  left: `${isDragging ? tempSliderMin : sliderMin}%`,
                  width: `${(isDragging ? tempSliderMax : sliderMax) - (isDragging ? tempSliderMin : sliderMin)}%`
                }}
              />
            </div>

            {/* Left handle */}
            <div
              className="absolute top-2 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-lg cursor-grab active:cursor-grabbing z-20 transition-all duration-150 ease-out"
              style={{ left: `calc(${isDragging ? tempSliderMin : sliderMin}% - 6px)` }}
              onMouseDown={(e) => {
                e.preventDefault();
                setIsDragging(true);
                const startX = e.clientX;
                const startPercent = tempSliderMin || sliderMin;

                const onMouseMove = (moveEvent: MouseEvent) => {
                  const parent = (e.target as HTMLElement).parentElement;
                  if (!parent) return;
                  const rect = parent.getBoundingClientRect();
                  const deltaX = moveEvent.clientX - startX;
                  const deltaPercent = (deltaX / rect.width) * 100;
                  const newMin = Math.max(0, Math.min(startPercent + deltaPercent, (tempSliderMax || sliderMax) - 1));
                  handleSliderDrag(newMin, tempSliderMax || sliderMax);
                };

                const onMouseUp = () => {
                  document.removeEventListener('mousemove', onMouseMove);
                  document.removeEventListener('mouseup', onMouseUp);
                  handleSliderRelease();
                };

                document.addEventListener('mousemove', onMouseMove);
                document.addEventListener('mouseup', onMouseUp);
              }}
              onTouchStart={(e) => {
                setIsDragging(true);
                const touch = e.touches[0];
                const startX = touch.clientX;
                const startPercent = tempSliderMin || sliderMin;

                const onTouchMove = (moveEvent: TouchEvent) => {
                  const parent = (e.target as HTMLElement).parentElement;
                  if (!parent) return;
                  const rect = parent.getBoundingClientRect();
                  const touch = moveEvent.touches[0];
                  const deltaX = touch.clientX - startX;
                  const deltaPercent = (deltaX / rect.width) * 100;
                  const newMin = Math.max(0, Math.min(startPercent + deltaPercent, (tempSliderMax || sliderMax) - 1));
                  handleSliderDrag(newMin, tempSliderMax || sliderMax);
                };

                const onTouchEnd = () => {
                  document.removeEventListener('touchmove', onTouchMove);
                  document.removeEventListener('touchend', onTouchEnd);
                  handleSliderRelease();
                };

                document.addEventListener('touchmove', onTouchMove);
                document.addEventListener('touchend', onTouchEnd);
              }}
            />

            {/* Right handle */}
            <div
              className="absolute top-2 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-lg cursor-grab active:cursor-grabbing z-20 transition-all duration-150 ease-out"
              style={{ left: `calc(${isDragging ? tempSliderMax : sliderMax}% - 6px)` }}
              onMouseDown={(e) => {
                e.preventDefault();
                setIsDragging(true);
                const startX = e.clientX;
                const startPercent = tempSliderMax || sliderMax;

                const onMouseMove = (moveEvent: MouseEvent) => {
                  const parent = (e.target as HTMLElement).parentElement;
                  if (!parent) return;
                  const rect = parent.getBoundingClientRect();
                  const deltaX = moveEvent.clientX - startX;
                  const deltaPercent = (deltaX / rect.width) * 100;
                  const newMax = Math.min(100, Math.max(startPercent + deltaPercent, (tempSliderMin || sliderMin) + 1));
                  handleSliderDrag(tempSliderMin || sliderMin, newMax);
                };

                const onMouseUp = () => {
                  document.removeEventListener('mousemove', onMouseMove);
                  document.removeEventListener('mouseup', onMouseUp);
                  handleSliderRelease();
                };

                document.addEventListener('mousemove', onMouseMove);
                document.addEventListener('mouseup', onMouseUp);
              }}
              onTouchStart={(e) => {
                setIsDragging(true);
                const touch = e.touches[0];
                const startX = touch.clientX;
                const startPercent = tempSliderMax || sliderMax;

                const onTouchMove = (moveEvent: TouchEvent) => {
                  const parent = (e.target as HTMLElement).parentElement;
                  if (!parent) return;
                  const rect = parent.getBoundingClientRect();
                  const touch = moveEvent.touches[0];
                  const deltaX = touch.clientX - startX;
                  const deltaPercent = (deltaX / rect.width) * 100;
                  const newMax = Math.min(100, Math.max(startPercent + deltaPercent, (tempSliderMin || sliderMin) + 1));
                  handleSliderDrag(tempSliderMin || sliderMin, newMax);
                };

                const onTouchEnd = () => {
                  document.removeEventListener('touchmove', onTouchMove);
                  document.removeEventListener('touchend', onTouchEnd);
                  handleSliderRelease();
                };

                document.addEventListener('touchmove', onTouchMove);
                document.addEventListener('touchend', onTouchEnd);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialChart;
